import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Upload, Loader2, Shield, Info, Camera } from "lucide-react";
import VerifiedBadgeChip from "../components/instructor/VerifiedBadgeChip";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InstructorBadgeUpload() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [badgeType, setBadgeType] = useState("adi");
  const [badgeNumber, setBadgeNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/instructor-badges/me`, { withCredentials: true });
      setMe(data);
      if (data?.badge_type) setBadgeType(data.badge_type);
      if (data?.badge_number) setBadgeNumber(data.badge_number);
      if (data?.expiry_date) setExpiryDate(data.expiry_date);
    } catch {
      toast.error("Couldn't load verification status.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleFile = (setter) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image must be under 6MB"); return; }
    setter(f);
  };

  const submit = async () => {
    if (!front || !back) { toast.error("Add both front and back photos."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("badge_type", badgeType);
      if (badgeNumber) fd.append("badge_number", badgeNumber);
      if (expiryDate) fd.append("expiry_date", expiryDate);
      fd.append("front", front);
      fd.append("back", back);
      await axios.post(`${API}/api/instructor-badges/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Submitted for review. We'll notify you once it's verified.");
      setFront(null); setBack(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-slate-950"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  const verified = me?.verified;
  const state = me?.state || "not_uploaded";

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="badge-upload-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Badge verification</h1>
            <p className="text-[11px] text-slate-500">Upload your official DVSA card — ADI (green) or PDI (pink).</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        <div data-testid="badge-status" className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-500" />
              <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500">Current status</p>
            </div>
            <VerifiedBadgeChip verification={me} size="lg" />
          </div>
          {verified && me?.expiry_date && (
            <p className="mt-2 text-[11px] text-slate-500">Expires {me.expiry_date}{typeof me.days_to_expiry === "number" ? ` · in ${me.days_to_expiry} days` : ""}.</p>
          )}
          {me?.renewal_reminder && (
            <p data-testid="badge-renewal-reminder" className="mt-2 text-[11px] font-bold text-amber-700 dark:text-amber-300">{me.renewal_reminder}</p>
          )}
          {state === "needs_update" && me?.rejection_reason && (
            <p data-testid="badge-rejection-reason" className="mt-2 text-[11px] font-bold text-amber-700 dark:text-amber-300">Admin note: {me.rejection_reason}</p>
          )}
        </div>

        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 space-y-3">
          <div className="flex items-start gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>We only show "Verified ADI" or "Verified PDI" publicly. Your photos stay private — admins and optionally your own learners can view them, nobody else.</p>
          </div>

          <div>
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Badge type</label>
            <div className="mt-1 flex gap-2">
              {[
                ["adi", "ADI (green)"],
                ["pdi", "PDI (pink)"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`badge-type-${k}`}
                  onClick={() => setBadgeType(k)}
                  className={`flex-1 px-3 py-2 text-xs font-extrabold rounded-xl border ${
                    badgeType === k
                      ? k === "adi"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-pink-600 text-white border-pink-600"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Badge number (optional)</span>
              <input data-testid="badge-number" value={badgeNumber} onChange={(e) => setBadgeNumber(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
            </label>
            <label className="block">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Expiry (optional)</span>
              <input data-testid="badge-expiry" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <FileTile testid="badge-front" label="Front" file={front} inputRef={frontRef} onPick={handleFile(setFront)} />
            <FileTile testid="badge-back"  label="Back"  file={back}  inputRef={backRef}  onPick={handleFile(setBack)} />
          </div>

          <button
            data-testid="badge-submit"
            onClick={submit}
            disabled={submitting || !front || !back}
            className="w-full px-4 py-3 text-sm font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Submit for verification
          </button>
        </div>
      </div>
    </div>
  );
}

function FileTile({ testid, label, file, inputRef, onPick }) {
  return (
    <label className="block">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{label}</span>
      <button
        type="button"
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        className="mt-1 w-full aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-indigo-400"
      >
        {file ? (
          <img src={URL.createObjectURL(file)} alt={label} className="w-full h-full object-cover rounded-xl" />
        ) : (
          <>
            <Camera className="w-5 h-5" />
            <span className="text-[11px] font-bold">Add {label.toLowerCase()} photo</span>
          </>
        )}
      </button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" />
    </label>
  );
}
